/**
 * Runs one operation across a bulk selection and reports how it went.
 *
 * SEQUENTIAL, NOT `Promise.all`. Every write behind a bulk bar here is a separate HTTP request to
 * a Cloud Run service, and firing thirty at once turns one careless click into a burst that the
 * service's own concurrency limit answers with failures that look like the operation being broken.
 * Sequential also makes the outcome legible: the failures are the tail, not a random subset.
 *
 * IT NEVER THROWS. A bulk action's honest result is a pair of counts — the caller reports both, and
 * a partial failure leaves the successful writes in place rather than being rolled back, because
 * there is no transaction across HTTP calls and pretending otherwise would be worse than saying
 * plainly that four of thirty did not go through.
 */
export async function runBulk<T>(
  items: readonly T[],
  operation: (item: T) => Promise<boolean>,
): Promise<{ succeeded: number; failed: number }> {
  let succeeded = 0;
  let failed = 0;

  for (const item of items) {
    try {
      if (await operation(item)) {
        succeeded += 1;
      } else {
        failed += 1;
      }
    } catch {
      failed += 1;
    }
  }

  return { succeeded, failed };
}
